import { Terrain, Animal } from "./types"
import { Tile } from "./tile"

export interface TutorialTrigger {
  level: number
  condition: "tile_in_market" | "tile_placed" | "predation_occurred" | "turn_start"
  filter?: { terrain?: Terrain; animal?: Animal }
  key: string
  message: string
  position: "top" | "bottom" | "near_market" | "near_grid"
  shown: boolean
}

interface TriggerDef {
  level: number
  condition: TutorialTrigger["condition"]
  filter?: { terrain?: Terrain; animal?: Animal }
  key: string
  position: TutorialTrigger["position"]
}

const TUTORIAL_TRIGGERS: TriggerDef[] = [
  // Level 0
  {
    level: 0,
    condition: "turn_start",
    key: "tutorial_0_place",
    position: "near_grid",
  },
  // Level 1
  {
    level: 1,
    condition: "tile_in_market",
    filter: { animal: Animal.Wolf },
    key: "tutorial_1_wolf",
    position: "near_market",
  },
  {
    level: 1,
    condition: "predation_occurred",
    key: "tutorial_1_predation",
    position: "near_grid",
  },
  // Level 2
  {
    level: 2,
    condition: "tile_placed",
    filter: { terrain: Terrain.Prairie },
    key: "tutorial_2_river",
    position: "near_grid",
  },
  {
    level: 2,
    condition: "tile_placed",
    filter: { terrain: Terrain.Prairie },
    key: "tutorial_2_cliff",
    position: "near_grid",
  },
  // Level 3
  {
    level: 3,
    condition: "tile_placed",
    filter: { terrain: Terrain.Forest },
    key: "tutorial_3_forest",
    position: "near_grid",
  },
  {
    level: 3,
    condition: "turn_start",
    key: "tutorial_3_combo",
    position: "top",
  },
]

export class TutorialManager {
  private triggers: TutorialTrigger[]
  private level: number

  constructor(level: number, messages: Record<string, string> = {}) {
    this.level = level
    // Deep clone triggers for this level, resolving messages from the dictionary
    this.triggers = TUTORIAL_TRIGGERS
      .filter(t => t.level === level)
      .map(t => ({
        ...t,
        message: messages[t.key] || t.key,
        filter: t.filter ? { ...t.filter } : undefined,
        shown: false,
      }))
  }

  /** Check triggers for a condition and return the first matching unshown message */
  check(
    condition: TutorialTrigger["condition"],
    context?: { tile?: Tile; predationOccurred?: boolean },
  ): TutorialTrigger | null {
    for (const trigger of this.triggers) {
      if (trigger.shown) continue
      if (trigger.condition !== condition) continue

      // Check filter
      if (trigger.filter) {
        if (trigger.filter.terrain && context?.tile?.terrain !== trigger.filter.terrain) continue
        if (trigger.filter.animal && context?.tile?.animal !== trigger.filter.animal) continue
      }

      // Special: predation_occurred only fires when predation actually happened
      if (condition === "predation_occurred" && !context?.predationOccurred) continue

      trigger.shown = true
      return trigger
    }
    return null
  }

  /** Check if any message is pending for market tiles */
  checkMarket(marketTiles: readonly Tile[]): TutorialTrigger | null {
    for (const tile of marketTiles) {
      const result = this.check("tile_in_market", { tile })
      if (result) return result
    }
    return null
  }

  /** Get all shown messages (for replay) */
  getShownMessages(): string[] {
    return this.triggers.filter(t => t.shown).map(t => t.message)
  }

  get isActive(): boolean {
    return this.level <= 3
  }
}
